import { formatCode, stickerDate } from '@pl/domain';
import type { BoxStickerSize, PalletStickerSize, SkuDto, StickerKind } from '@pl/domain';
import { useLayoutEffect, useRef } from 'react';
import { QrCode } from '../../ui/QrCode';
import s from './Stickers.module.css';

/**
 * Nothing on this screen can show a real code: the API reserves codes when the batch is created,
 * so before the POST there is nothing to draw. The preview therefore always carries one obvious
 * placeholder, and the panel labels it "Sample".
 *
 * Both are the real 10-character shape — kind letter + 8 + check character, printed 5-5 — but
 * carry `0` where the check character belongs (`B00000000B` and `P00000000P` are the codes that
 * would pass). A scanner reading the sample QR off a screen therefore gets `INVALID_CODE` and can
 * never resolve to a real box or pallet.
 */
export const SAMPLE_BOX_CODE = 'B000000000';
export const SAMPLE_PALLET_CODE = 'P000000000';

/**
 * Each sticker is drawn at the artwork size from `mockups/stickers-01-designs.html` and then
 * scaled as a whole. Drawing at the artwork size and scaling — rather than re-deriving every
 * type size for a smaller box — is what keeps the proportions exactly right: the drawn rectangle
 * carries the label's true ratio to the pixel, and the colour block stays the same fraction of
 * the label that it will be on the thermal print.
 *
 * The two box sizes share one 300px artwork height, so every type size below stays the same
 * fraction of the label's height at either size — which is how the printer sizes type too
 * (`apps/api/src/modules/labels/pdf.ts` derives from `h`). A `4x2` label is simply the wider
 * drawing its 2:1 ratio calls for; the dimension rules beside it state the real inches.
 */
const ART = {
  '2.25x1.25': { w: 540, h: 300, scale: 0.85, across: '2.25 in', down: '1.25 in', qr: 225 },
  '4x2': { w: 600, h: 300, scale: 0.85, across: '4 in', down: '2 in', qr: 225 },
  pallet: { w: 400, h: 600, scale: 0.58, across: '4 in', down: '6 in', qr: 250 },
  /**
   * A pallet sticker on a 4 × 2 in roll, drawn after `pdf.ts` drawPalletWide at 300 px per 144 pt
   * (2.08 px/pt): the QR is the label's height less a 7% margin top and bottom, and the band, code,
   * SKU and footer stack beside it.
   */
  pallet4x2: { w: 600, h: 300, scale: 0.85, across: '4 in', down: '2 in', qr: 258 },
} as const;

/** Artwork type sizes, in the drawing's own pixels. `Fit` shrinks from these, never past them. */
const TYPE = { name: 29, colour: 96, code: 27, meta: 16, blankCode: 35, palletWideCode: 36, palletWideName: 22, palletWideColour: 42, palletWideMeta: 19 };

/**
 * A sticker is a physical object: the label cannot grow, so a line that does not fit has to
 * lose type size. It must never lose letters — on a cable warehouse floor the colour IS the
 * product, and half a colour name is worse than a smaller one. Measured rather than guessed
 * from a character count, because the artwork uses a variable font at several widths.
 *
 * The measured size always fits, so there is no floor by default: `colourName` is 60 characters
 * of free text per the API contract, and a floor there would only put white letters back outside
 * the black block, which is the invisible-colour bug this measuring exists to kill. The real
 * printer agrees — `pdf.ts` walks the same line down in 0.5pt steps to 5pt with `lineBreak:
 * false` — and where the two disagree the printer is right. `floor` is only for a line that is
 * allowed to clip instead (the SKU name), where stopping at a readable size beats shrinking.
 */
function useFitText(base: number, text: string, floor = 0) {
  const ref = useRef<HTMLSpanElement>(null);
  useLayoutEffect(() => {
    const span = ref.current;
    const box = span?.parentElement;
    if (!span || !box) return;
    const fit = () => {
      // offsetWidth/clientWidth are layout pixels, so the enclosing `transform: scale()` that
      // shrinks the whole sticker does not distort the comparison.
      span.style.fontSize = `${base}px`;
      const style = getComputedStyle(box);
      // One pixel of slack: offsetWidth is rounded, and trailing letter-spacing is not counted in it.
      const room = box.clientWidth - parseFloat(style.paddingLeft) - parseFloat(style.paddingRight) - 1;
      const wanted = span.offsetWidth;
      if (room <= 0 || wanted <= room) return;
      const min = Math.max(1, base * floor);
      // The proportional guess gets within a few pixels but is only an estimate: letter-spacing and
      // the variable font's width axis do not scale linearly with size. Walk the rest of the way
      // down the way the printer does (`pdf.ts` fitFont, 0.5pt steps), so the fit is measured to the
      // end rather than assumed. Unfloored, this always lands inside the box.
      let size = Math.max(min, Math.floor((base * room) / wanted));
      span.style.fontSize = `${size}px`;
      while (size > min && span.offsetWidth > room) {
        size = Math.max(min, size - 1);
        span.style.fontSize = `${size}px`;
      }
    };
    fit();
    // The artwork is set in Archivo; a first measurement can land before that face arrives.
    let live = true;
    void document.fonts?.ready.then(() => {
      if (live) fit();
    });
    return () => {
      live = false;
    };
  }, [base, text, floor]);
  return ref;
}

function Fit({ className, size, floor, children }: { className?: string; size: number; floor?: number; children: string }) {
  const ref = useFitText(size, children, floor);
  return (
    <div className={className}>
      <span ref={ref} className={s.stFit}>
        {children}
      </span>
    </div>
  );
}

export function StickerPreview({
  kind,
  sku,
  siteCode,
  timeZone,
  boxSize = '2.25x1.25',
  palletSize = '4x6',
  now = new Date(),
}: {
  kind: StickerKind;
  sku: SkuDto | null;
  siteCode: string | null;
  timeZone?: string;
  boxSize?: BoxStickerSize;
  palletSize?: PalletStickerSize;
  now?: Date;
}) {
  const wide = kind === 'pallet' && palletSize === '4x2';
  const art = kind === 'pallet' ? (wide ? ART.pallet4x2 : ART.pallet) : ART[boxSize];
  const sample = kind === 'pallet' ? SAMPLE_PALLET_CODE : SAMPLE_BOX_CODE;
  const code = formatCode(sample);
  const where = siteCode ?? 'No site';
  const when = stickerDate(now, timeZone);

  return (
    <div className={s.stDim}>
      <div className={s.stDimW}>
        <span>{art.across}</span>
      </div>
      <div className={s.stDimH}>
        <span>{art.down}</span>
      </div>
      <div className={s.stStage} style={{ width: art.w * art.scale, height: art.h * art.scale }}>
        <div
          className={`${s.stLabel} ${wide ? s.stPwL : kind === 'pallet' ? s.stPalL : kind === 'blank' ? s.stBlankL : s.stBoxL}`}
          style={{ width: art.w, height: art.h, transform: `scale(${art.scale})` }}
        >
          {kind !== 'pallet' && (
            <span className={s.stQr}>
              <QrCode value={sample} size={art.qr} />
            </span>
          )}

          {kind === 'sku' && (
            <div className={s.stTxt}>
              <Fit className={s.stName} size={TYPE.name} floor={0.62}>
                {sku ? sku.name.toUpperCase() : 'SKU NAME'}
              </Fit>
              {/* Inverted block: on a warehouse floor the colour is the product, and a solid
                  black field is the only thing a one-colour thermal print can do that still
                  reads across a room. */}
              <Fit className={s.stColor} size={TYPE.colour}>
                {(sku?.colourName ?? 'Color').toUpperCase()}
              </Fit>
              {/* The printer fits this line too (`pdf.ts` drawBox), so the preview does the same. */}
              <Fit className={s.stCode} size={TYPE.code}>
                {code}
              </Fit>
              <Fit className={s.stMeta} size={TYPE.meta}>
                {`${where} · ${when} · LB-next`}
              </Fit>
            </div>
          )}

          {kind === 'blank' && (
            <Fit className={s.stBCode} size={TYPE.blankCode}>
              {code}
            </Fit>
          )}

          {wide && (
            <>
              <span className={s.stQr}>
                <QrCode value={sample} size={art.qr} />
              </span>
              <div className={s.stPwCol}>
                <div className={s.stPwBand}>PALLET</div>
                <Fit className={s.stPwCode} size={TYPE.palletWideCode}>
                  {code}
                </Fit>
                {sku && (
                  <>
                    <Fit className={s.stPwName} size={TYPE.palletWideName} floor={0.62}>
                      {sku.name.toUpperCase()}
                    </Fit>
                    {sku.colourName && (
                      <Fit className={s.stPwColor} size={TYPE.palletWideColour}>
                        {sku.colourName.toUpperCase()}
                      </Fit>
                    )}
                  </>
                )}
                <Fit className={s.stPwMeta} size={TYPE.palletWideMeta}>
                  {`${where} · ${when}`}
                </Fit>
              </div>
            </>
          )}

          {kind === 'pallet' && !wide && (
            <>
              <div className={s.stBand}>PALLET</div>
              <span className={`${s.stQr} ${s.stPQr}`}>
                <QrCode value={sample} size={art.qr} />
              </span>
              <div className={s.stPCode}>{code}</div>
              <div className={s.stRule} />
              {sku && (
                <div className={s.stPSku}>
                  <div className={s.stPName}>{sku.name.toUpperCase()}</div>
                  {sku.colourName && <div className={s.stPColor}>{sku.colourName.toUpperCase()}</div>}
                </div>
              )}
              <div className={s.stPMeta}>
                {where} · {when}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
